/**
 * «Tall for måneden» under the month (owner 2026-10-27, prototype foot-m.prototype.html after two reviews): the
 * week's rows with one column per week and «Måned» at the right, the same rules as Dag and Uke (figures.ts), and
 * behind «Diagram» «Lønn mot mål», how far over or under the target the month is, added up day by day. A day that
 * is over counts what was clocked, the rest the plan, so the month column is where the month lands.
 */
import type { ApiShift } from '@/api/data';
import type { CostDay } from '@/api/labour';
import { T } from '@/i18n';
import type { NeedProfile } from '@/shell/local';
import { isoWeek, mondayOf } from '@timefly/core';
import { ChartsButton, FoldButton, MissLine, useNotUsed, useStored } from './FiguresBits';
import css from './MonthFigures.module.css';
import wf from './WeekFigures.module.css';
import { hourCover, hourRange } from './chartFigures';
import { type DayNumbers, dayNumbers, h1, hoursOff, kr, missing, p1, pt, sumDays, verdict } from './figures';
import { type SalesDay, needHours } from './staffing';

type Sum = ReturnType<typeof sumDays>;

export function MonthFigures({
  days,
  today,
  need,
  target,
  targetSet,
  shifts,
  cost,
  sales,
  money,
  tenantId,
  onSales,
}: {
  days: string[];
  today: string;
  need: NeedProfile;
  target: number;
  targetSet: boolean;
  shifts: ApiShift[];
  cost: Map<string, CostDay>;
  sales: Record<string, SalesDay>;
  money: boolean;
  tenantId: number | undefined;
  /** the sales are typed in the week: «Omsetning» in «Mangler» goes there */
  onSales: () => void;
}) {
  const [open, setOpen] = useStored('vaktplan.month.open', true);
  const [charts, setCharts] = useStored('vaktplan.month.charts', false);
  const [notUsed, setNotUsed] = useNotUsed(tenantId);

  const needSet = days.some((d) => needHours(need, d) != null);
  const needOn = needSet && !notUsed.includes('need');
  const salesUsed = money && !notUsed.includes('sales');
  const all = days.map((d) => dayNumbers(d, { today, tmode: undefined, need, needOn, shifts, cost, sales }));
  const anySales = all.some((x) => x.f.sales != null);
  const showMoney = salesUsed && anySales;
  const m = sumDays(all);
  const over = (p: number | null) => targetSet && !m.unpriced && p != null && p > target + 1;
  const miss = missing({ needSet, money, anySales, targetSet, unpriced: m.unpriced, notUsed });
  const tag = days.some((d) => d >= today) ? T('prognose') : '';

  // the weeks of the month, Monday to Sunday, the first and last ones cut at the month's edge
  const weeks: { n: number; xs: DayNumbers[] }[] = [];
  for (const x of all) {
    const n = isoWeek(mondayOf(x.d));
    const last = weeks[weeks.length - 1];
    if (last?.n === n) last.xs.push(x);
    else weeks.push({ n, xs: [x] });
  }
  const ws = weeks.map((w) => ({ ...w, t: sumDays(w.xs) }));
  const cur = weeks.findIndex((w) => w.xs.some((x) => x.d === today));

  // «Dekket»: the hours with enough people over the month
  const [h0, h1x] = hourRange(need, days, shifts);
  const cover = needOn
    ? days.flatMap((d) => hourCover(need, d, shifts, h0, h1x)).filter((z) => z.need > 0)
    : [];
  const covered = cover.filter((z) => z.on + 1e-6 >= z.need).length;
  const coverTxt = cover.length > 0 && (
    <span>
      {T('Dekket')}{' '}
      <b className={covered < cover.length ? wf.warn : undefined}>
        {T('{a} av {b} timer', { a: covered, b: cover.length })}
      </b>
    </span>
  );
  const missLine = <MissLine miss={miss} onHide={(k) => setNotUsed([...notUsed, k])} onSales={onSales} />;
  const style = { '--nw': ws.length } as React.CSSProperties;

  const hoursOf = (t: Sum) =>
    needOn ? (
      <span className={hoursOff(t.h, t.n) ? wf.warn : undefined}>
        {h1(t.h)} av {h1(t.n)} t
      </span>
    ) : (
      `${h1(t.h)} t`
    );
  const pctOf = (t: Sum) =>
    t.pct == null ? (
      <span className={wf.sub}>–</span>
    ) : (
      <>
        <span className={over(t.pct) ? wf.warn : undefined}>
          {p1(t.pct)}
          {t.unpriced ? '*' : ''}
        </span>
        {targetSet && !t.unpriced && <span className={wf.sub}>{verdict(t.pc, t.s, target)}</span>}
      </>
    );
  const row = (key: string, label: React.ReactNode, cell: (t: Sum) => React.ReactNode) => (
    <div className={css.row} style={style} key={key}>
      <div className={css.lab}>{label}</div>
      {ws.map((w) => (
        <div key={w.n} className={css.cell}>
          {cell(w.t)}
        </div>
      ))}
      <div className={`${css.cell} ${css.tot}`}>{cell(m)}</div>
    </div>
  );

  return (
    <section className={css.wrap} data-testid="month-figures" aria-label={T('Tall for måneden')}>
      <div className={css.head}>
        <FoldButton open={open} onClick={() => setOpen(!open)} label={T('Tall for måneden')} />
        {!open && (
          <span className={css.line}>
            {coverTxt}
            {showMoney && m.pct != null ? (
              <span>
                {T('Lønn %')} <b className={over(m.pct) ? wf.warn : undefined}>{p1(m.pct)}</b>
                {tag && <span className={wf.sub}> {tag}</span>}
              </span>
            ) : (
              <span>
                {T('Timer')} <b>{h1(m.h)} t</b>
              </span>
            )}
          </span>
        )}
        {missLine}
        <span className={wf.sp} />
        {open && showMoney && targetSet && <ChartsButton open={charts} onClick={() => setCharts(!charts)} />}
      </div>
      {open && (
        <>
          <div className={css.row} style={style}>
            <div className={css.lab} />
            {ws.map((w, i) => (
              <div key={w.n} className={`${css.cell} ${css.wkh}${i === cur ? ` ${css.cur}` : ''}`}>
                {T('Uke {n}', { n: w.n })}{' '}
                <span className={wf.sub}>
                  {Number(w.xs[0]?.d.slice(8))}.-{Number(w.xs[w.xs.length - 1]?.d.slice(8))}.
                </span>
              </div>
            ))}
            <div className={`${css.cell} ${css.tot} ${css.wkh}`}>
              {T('Måned')} {tag && <span className={wf.sub}>{tag}</span>}
            </div>
          </div>
          {row('hours', T('Timer'), hoursOf)}
          {showMoney && row('sales', T('Omsetning'), (t) => (t.s ? `${t.e ? '≈ ' : ''}${kr(t.s)} kr` : '–'))}
          {money && row('cost', T('Lønnskost'), (t) => `${kr(t.c)} kr${t.unpriced ? '*' : ''}`)}
          {showMoney &&
            row(
              'pct',
              <>
                {T('Lønn %')} {targetSet && <span className={wf.sub}>{T('mål {p}', { p: pt(target) })}</span>}
              </>,
              pctOf,
            )}
          {charts && showMoney && targetSet && (
            <GoalChart
              all={all}
              weeks={ws.map((w) => w.xs.length)}
              today={today}
              target={target}
              style={style}
            />
          )}
        </>
      )}
    </section>
  );
}

/** «Lønn mot mål»: over (+) or under (−) the target in kroner, added up day by day; what happened solid, the plan dashed */
function GoalChart({
  all,
  weeks,
  today,
  target,
  style,
}: {
  all: DayNumbers[];
  weeks: number[];
  today: string;
  target: number;
  style: React.CSSProperties;
}) {
  let acc = 0;
  const pts = all.map((x) => {
    if (x.f.sales) acc += x.f.cost - (x.f.sales * target) / 100;
    return acc;
  });
  const W = 1000;
  const H = 80;
  const hi = Math.max(0, ...pts) * 1.1;
  const lo = Math.min(0, ...pts) * 1.1;
  const span = hi - lo || 1;
  // each week gets its column's width, so the line sits under its week
  const xs: number[] = [];
  weeks.forEach((len, w) => {
    for (let k = 0; k < len; k++) xs.push(((w + (k + 0.5) / len) / weeks.length) * W);
  });
  const y = (v: number) => 4 + ((hi - v) / span) * (H - 8);
  const t = all.filter((x) => x.d < today).length;
  const path = (a: number, b: number) =>
    pts
      .slice(a, b)
      .map((v, k) => `${k ? 'L' : 'M'}${(xs[a + k] ?? 0).toFixed(1)} ${y(v).toFixed(1)}`)
      .join('');
  const area = (pos: boolean) =>
    `M${xs[0]} ${y(0)}${pts.map((v, k) => `L${(xs[k] ?? 0).toFixed(1)} ${y(pos ? Math.max(0, v) : Math.min(0, v)).toFixed(1)}`).join('')}L${xs[xs.length - 1]} ${y(0)}Z`;
  const end = pts[pts.length - 1] ?? 0;
  return (
    <div className={css.row} style={style}>
      <div className={`${css.lab} ${css.key}`}>
        <b>{T('Lønn mot mål')}</b>
        <span>
          <i className={css.kPos} />
          {T('Over målet')}
        </span>
        <span>
          <i className={css.kNeg} />
          {T('Under')}
        </span>
      </div>
      <div className={css.goal}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={T('Lønn mot målet, lagt sammen dag for dag')}
        >
          {weeks.slice(1).map((_, k) => (
            <line
              key={`sep${(k + 1) / weeks.length}`}
              className={css.sep}
              x1={((k + 1) / weeks.length) * W}
              x2={((k + 1) / weeks.length) * W}
              y1={0}
              y2={H}
            />
          ))}
          <path className={css.pos} d={area(true)} />
          <path className={css.neg} d={area(false)} />
          <line className={css.zero} x1={0} x2={W} y1={y(0)} y2={y(0)} />
          {t > 0 && <path className={css.ln} d={path(0, t)} />}
          {t < pts.length && <path className={css.lf} d={path(Math.max(0, t - 1), pts.length)} />}
        </svg>
      </div>
      <div className={`${css.cell} ${css.tot} ${css.end}`}>
        <span className={wf.sub}>{T('ved månedsslutt')}</span>
        <b className={end > 0 ? wf.warn : undefined}>
          {end > 0 ? T('{kr} kr over', { kr: kr(end) }) : T('{kr} kr under', { kr: kr(-end) })}
        </b>
      </div>
    </div>
  );
}
